import {call, delay, put, spawn, takeEvery} from 'redux-saga/effects';
import {PayloadAction} from "@reduxjs/toolkit";
import {api} from "../../api/api";
import {CardGenerationStateDto} from "../../common";
import {CardStore} from "../cards.interface";
import {cardsActions, SetGenerationPayload} from "../cards.slice";

const POLL_INTERVAL_MS = 3000;
const MAX_CONSECUTIVE_FAILURES = 5;

/**
 * Regenerate/invert run in a server-side Workflow, so nothing here is tied to
 * the card page: any card that reaches the store with a running generation is
 * watched until it finishes, and then reloaded. Opening the card again (even
 * after a page reload) brings the running generation back into the store and
 * resumes watching.
 */
function* watchGeneration(cardId: string) {
    let failures = 0;

    for (;;) {
        yield delay(POLL_INTERVAL_MS);

        try {
            const state: CardGenerationStateDto = yield call(api.cardGeneration, cardId);
            failures = 0;
            if (state.generation?.status !== 'running') break;
        } catch {
            failures += 1;
            if (failures >= MAX_CONSECUTIVE_FAILURES) return;
        }
    }

    yield put(cardsActions.loadCard(cardId));
}

function runningCardIds(action: PayloadAction<CardStore[] | SetGenerationPayload>): string[] {
    const cards = Array.isArray(action.payload) ? action.payload : [action.payload];
    return cards
        .filter(card => card.generation?.status === 'running')
        .map(card => card.cardId);
}

export function* cardGenerationSaga() {
    const watched = new Set<string>();

    function* watch(cardId: string) {
        try {
            yield call(watchGeneration, cardId);
        } finally {
            watched.delete(cardId);
        }
    }

    yield takeEvery(
        [cardsActions.put.type, cardsActions.setGeneration.type],
        function* (action: PayloadAction<CardStore[] | SetGenerationPayload>) {
            for (const cardId of runningCardIds(action)) {
                if (watched.has(cardId)) continue;
                watched.add(cardId);
                yield spawn(watch, cardId);
            }
        },
    );
}
